// Copyright 2016 Sorama B.V.

import {
    autoinject,
    bindable,
    BindingEngine,
    bindingMode,
    computedFrom,
    customElement,
    Disposable,
    DOM,
    observable,
} from "aurelia-framework";
import { ClientWorkflowModuleCatalog } from "catalogs/WorkflowModuleCatalog/ClientWorkflowModuleCatalog";
import { ShellViewManager } from "modules/shell/App/ShellViewManager";
import { IHardwareDevice } from "services/Hardware/Datatypes/IHardwareDevice";
import { MeasurementDeviceSetup } from "services/Hardware/Datatypes/MeasurementDeviceSetup";

@customElement("device-selector")
@autoinject
export class DeviceSelector {
    //#region Injected Services
    private element: HTMLElement;
    private bindingEngine: BindingEngine;
    //#endregion

    //#region Bindable Properties
    @bindable
    public availableDevices: IHardwareDevice[];

    @bindable
    public availableSetups: MeasurementDeviceSetup[];

    @bindable({ defaultBindingMode: bindingMode.oneWay })
    public refreshing: boolean;

    @bindable
    public disabled: boolean;

    @bindable({ defaultBindingMode: bindingMode.twoWay })
    public selectedDeviceId = "";

    @bindable({ defaultBindingMode: bindingMode.twoWay })
    public selectedSetupId = "";
    //#endregion

    //#region Computed Properties
    @computedFrom("availableDevices", "availableDevices.length", "lastViewedDevices")
    public get numberOfNewDevices(): number {
        if (this.availableDevices)
            return this.availableDevices.filter((x) => this.lastViewedDevices.every((y) => y != x.id)).length;

        return 0;
    }

    @computedFrom("selectedDevice", "availableDevices.length", "selectedSetup", "availableSetups.length")
    public get selectBoxContent(): string {
        if (
            (!this.availableDevices || this.availableDevices.length == 0) &&
            (!this.availableSetups || this.availableSetups.length == 0)
        ) {
            return "No devices or setups available";
        } else if (this.selectedDevice) {
            return this.selectedDevice.name;
        } else if (this.selectedSetup) {
            return this.selectedSetup.name;
        } else {
            return (
                "Select a " +
                (this.availableDevices && this.availableDevices.length > 0 ? "device" : "") +
                (this.availableDevices &&
                this.availableDevices.length > 0 &&
                this.availableSetups &&
                this.availableSetups.length > 0
                    ? " or "
                    : "") +
                (this.availableSetups && this.availableSetups.length > 0 ? "setup" : "")
            );
        }
    }
    //#endregion

    //#region Private Properties
    @observable
    private selectedDevice: IHardwareDevice = null;

    @observable
    private selectedSetup: MeasurementDeviceSetup = null;

    private availableDevicesListener: Disposable;
    private lastViewedDevices: string[] = [];
    //#endregion

    //#region Constructor
    constructor(
        bindingEngine: BindingEngine,
        private readonly shellViewManager: ShellViewManager
    ) {
        this.bindingEngine = bindingEngine;
    }
    //#endregion

    private selectDevice(device: IHardwareDevice) {
        if (device.name.includes("iv64")) {
            this.shellViewManager.openRightSideBar(ClientWorkflowModuleCatalog.DeviceManagementModule, [
                {
                    name: "frontEndId",
                    value: device.id,
                },
            ]);
        }
        this.selectedDeviceIdChanged(device.id);
    }

    //#region Property Changed Callbacks
    private availableDevicesChanged(devices: IHardwareDevice[]): void {
        if (this.availableDevicesListener) {
            this.availableDevicesListener.dispose();
            this.availableDevicesListener = undefined;
        }

        if (devices) {
            this.availableDevicesListener = this.bindingEngine
                .collectionObserver(devices)
                .subscribe(() => this.availableDevicesCollectionChanged());
        }
    }

    private availableDevicesCollectionChanged(): void {
        const devices = this.availableDevices;

        /* Update selected device */
        if (
            (!this.selectedDevice && this.selectedDeviceId) ||
            (this.selectedDevice && this.selectedDevice.id != this.selectedDeviceId)
        ) {
            // The selected device ID has been changed through the binding, but it was not known
            // previously. Re-check if it is valid now.
            for (let i = 0; i < devices.length; i += 1) {
                if (this.selectedDeviceId == devices[i].id) {
                    this.selectedDevice = devices[i];
                    return;
                }
            }

            // It is not known; fall-through to default behaviour below.
        }

        if (!this.selectedDevice) return;

        // Test if the selected device is still connected
        for (let i = 0; i < devices.length; i += 1) {
            if (this.selectedDevice.id == devices[i].id) {
                // This device is currently selected; keep it
                this.selectedDevice = devices[i]; // Update the object so === will not break
                return;
            }
        }
    }

    private selectedDeviceIdChanged(value: string): void {
        if (!this.availableDevices || this.availableDevices.length === 0) {
            return;
        }

        const devices = this.availableDevices;

        // Unselect when no ID is provided
        if (!value) {
            if (this.selectedDevice) this.selectedDevice = null;
            return;
        }

        if (this.selectedDevice && this.selectedDevice.id === value) return;

        // Check if we know the provided ID
        for (let i = 0; i < devices.length; i += 1) {
            if (value === devices[i].id) {
                // We do; update the selected device
                this.selectedDevice = devices[i];
                return;
            }
        }

        // We don't; refresh
        this.updateDevices();
    }

    private selectedSetupIdChanged(value: string): void {
        if (!this.availableSetups || this.availableSetups.length === 0) {
            return;
        }

        const setups = this.availableSetups;

        // Unselect when no ID is provided
        if (!value) {
            if (this.selectedSetup) this.selectedSetup = null;
            return;
        }

        if (this.selectedSetup && this.selectedSetup.id === value) return;

        // Check if we know the provided ID
        for (let i = 0; i < setups.length; i += 1) {
            if (value === setups[i].id) {
                // We do; update the selected device
                this.selectedSetup = setups[i];
                return;
            }
        }

        // We don't; refresh
        this.updateDevices();
    }

    /**
     * Gets called from the ui when the user selects a device
     */
    private selectedDeviceChanged(value: IHardwareDevice): void {
        if (value) {
            this.selectedSetup = null;

            if (this.selectedDeviceId !== value.id) {
                this.selectedDeviceId = value.id;
            }
        } else {
            this.selectedDeviceId = null;
        }
    }

    /**
     * Gets called from the ui when the user selects a setup
     */
    private selectedSetupChanged(value: MeasurementDeviceSetup): void {
        if (value) {
            this.selectedDevice = null;

            if (this.selectedSetupId !== value.id) {
                this.selectedSetupId = value.id;
            }
        } else {
            this.selectedSetupId = null;
        }
    }
    //#endregion

    //#region Helper Methods

    // Functions for interactions with hardware service
    private updateDevices(): void {
        const event = DOM.createCustomEvent("refresh-action", { bubbles: true, cancelable: true });

        if (this.element) this.element.dispatchEvent(event);
    }

    private dropdownClicked(): void {
        this.lastViewedDevices = this.availableDevices.map((x) => x.id);
    }
    //#endregion
}
